import { EmailAddressZ } from "@sellar-io/common";
import {
  Button,
  InputEmail,
  InputField,
  InputNote,
} from "@sellar-io/components";
import { useForm } from "@tanstack/react-form";
import { useStore } from "@tanstack/react-store";
import { useId, useState, type ReactNode } from "react";
import { z } from "zod";
import { isHttpError } from "../../../utils/httpClient";

const invitationSchema = z.object({ email: EmailAddressZ });

export const BusinessInvitationForm = ({
  businessName,
  onInvite,
  children,
}: {
  businessName: string;
  onInvite: (email: string) => Promise<unknown>;
  children?: (form: ReactNode) => ReactNode;
}) => {
  const errorId = useId();
  const [error, setError] = useState<string | null>(null);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const form = useForm({
    defaultValues: { email: "" },
    validators: { onSubmit: invitationSchema },
    onSubmit: async ({ value }) => {
      setError(null);
      setInvitedEmail(null);
      const { email } = invitationSchema.parse(value);
      try {
        await onInvite(email);
        form.reset();
        setInvitedEmail(email);
      } catch (cause) {
        const message = isHttpError<{ message?: unknown }>(cause)
          ? cause.response?.data?.message
          : undefined;
        setError(
          typeof message === "string" && message.length > 0
            ? message
            : "We couldn't send the invitation. Please try again.",
        );
      }
    },
  });

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  const content = (
    <form
      noValidate
      className="flex flex-col gap-2 border-t border-zinc-200 bg-zinc-50 p-4 pb-6"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!form.state.isSubmitting) {
          form.handleSubmit();
        }
      }}
    >
      <div>
        <h3 className="text-base leading-6 font-semibold">Invite a teammate</h3>
        <p className="text-sm leading-5 text-zinc-500">
          Invite someone to place orders for {businessName}
        </p>
      </div>
      <form.Field name="email">
        {(field) => (
          <InputField
            label="Teammate email address"
            labelClassName="sr-only"
            containerClassName="rounded-lg p-3"
            note={
              field.state.meta.errors.length > 0 && (
                <div id={errorId} role="alert">
                  <InputNote className="text-red-700">
                    Enter a valid email address.
                  </InputNote>
                </div>
              )
            }
          >
            <InputEmail
              name={field.name}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) => {
                field.handleChange(event.target.value);
                setError(null);
                setInvitedEmail(null);
              }}
              disabled={isSubmitting}
              placeholder="Enter email address"
              autoComplete="email"
              required
              aria-invalid={field.state.meta.errors.length > 0}
              aria-describedby={
                field.state.meta.errors.length > 0 ? errorId : undefined
              }
              className="text-sm leading-5"
            />
          </InputField>
        )}
      </form.Field>
      {error && (
        <p role="alert" className="text-sm leading-5 text-red-700">
          {error}
        </p>
      )}
      {invitedEmail && (
        <p role="status" className="text-sm leading-5 text-green-700">
          Invitation sent to {invitedEmail}.
        </p>
      )}
      <Button
        type="submit"
        color="purple"
        loading={isSubmitting}
        className="min-h-9 w-full rounded-lg bg-[#8c23c7] px-3 py-2 text-sm leading-5 font-medium tracking-normal normal-case shadow-[0_2px_1px_rgba(0,0,0,0.15)] hover:bg-[#7b1eaf]"
      >
        Send invitation
      </Button>
    </form>
  );

  return children ? children(content) : content;
};
